import type {
	NotificationScope,
	NotificationTypeDefinition,
	ResolvedTypePreference,
} from "@productlane/lib/src/browser"
import type { NotificationPreferencePatch } from "@productlane/zero/mutators/notifications"
import { NOTIFICATION_SCOPE_COPY } from "@productlane/lib/src/browser"

import { NOTIFICATION_SETTINGS_COPY as copy } from "@/screens/app/notifications/copy"

/** Select values: a scope, "unassigned", or "view:<id>" for a saved view's filter. */
export type ScopeOptionValue =
	| NotificationScope
	| "unassigned"
	| `view:${string}`

export interface ScopeOption {
	value: ScopeOptionValue
	label: string
	/** Saved view's icon name, rendered with NucleoFillIcon. */
	icon?: string
	groupLabel?: string
}

// Matches the inbox sidebar's fallback for a view without an icon.
const DEFAULT_VIEW_ICON = "Layers2"

const UNASSIGNED_FILTER = { assigneeIds: [null] }

export function scopeOptionsFor(
	def: NotificationTypeDefinition,
	views: readonly { id: string; name: string; icon?: string | null }[],
): ScopeOption[] {
	const options: ScopeOption[] = def.scopes.map((scope) => ({
		value: scope,
		label: NOTIFICATION_SCOPE_COPY[scope].label,
	}))
	// Thread filters only mean something for thread events.
	if (def.scopes.includes("all") && def.entityType === "thread") {
		options.push({ value: "unassigned", label: copy.scopeOptions.unassigned })
		views.forEach((view, index) => {
			options.push({
				value: `view:${view.id}`,
				label: view.name,
				icon: view.icon ?? DEFAULT_VIEW_ICON,
				groupLabel: index === 0 ? copy.scopeOptions.viewsHeading : undefined,
			})
		})
	}
	return options
}

export function scopeOptionValue(
	pref: ResolvedTypePreference,
): ScopeOptionValue {
	if (pref.inbox !== "all" || !pref.filter) return pref.inbox
	if (typeof pref.filter.viewId === "string")
		return `view:${pref.filter.viewId}`
	if (JSON.stringify(pref.filter) === JSON.stringify(UNASSIGNED_FILTER)) {
		return "unassigned"
	}
	return "all"
}

export function patchForScopeOption(
	value: ScopeOptionValue,
): NotificationPreferencePatch {
	if (value === "unassigned") return { inbox: "all", filter: UNASSIGNED_FILTER }
	if (value.startsWith("view:")) {
		return { inbox: "all", filter: { viewId: value.slice("view:".length) } }
	}
	if (value === "all") return { inbox: "all", filter: null }
	return { inbox: value as NotificationScope }
}

/** Off and Involved only, with nothing else to pick: an on/off choice that reads better as a switch. */
export function isOnOffScope(options: readonly ScopeOption[]): boolean {
	return (
		options.length === 2 &&
		options.some((o) => o.value === "off") &&
		options.some((o) => o.value === "involved")
	)
}
